// router/index.js
import { createRouter, createWebHistory } from 'vue-router'
import * as coreApi from '../api/core'
import { setEmbedToken } from '../api/common'
import { store } from '../store'

// Import route groups
import adminRoutes from './routes/admin'
import coreRoutes from './routes/core'
import messagingRoutes from './routes/messaging'
import formsRoutes from './routes/forms'
import faxingRoutes from './routes/faxing'
import patientRoutes from './routes/patient'
import taskRoutes from './routes/tasks'
import reportingRoutes from './routes/reporting'
import telehealthRoutes from './routes/telehealth'
import trainingRoutes from './routes/training'

const routes = [
  {
    path: '/',
    redirect: '/login'
  },
  {
    path: '/admin',
    redirect: '/admin/accounts'
  },
  {
    path: '/contacts',
    redirect: '/contacts/patients'
  },
  {
    path: '/register/:slug',
    name: 'Registration',
    component: () => import(/* webpackChunkName: "auth" */ '@/views/registration/RegistrationView.vue')
  },
  ...adminRoutes,
  ...coreRoutes,
  ...messagingRoutes,
  ...formsRoutes,
  ...faxingRoutes,
  ...patientRoutes,
  ...taskRoutes,
  ...reportingRoutes,
  ...telehealthRoutes,
  ...trainingRoutes
]

const router = createRouter({
  routes,
  history: createWebHistory()
})

// Query parameter preservation — embed/chrome flags only. Patient focus is
// deliberately NOT preserved: it belongs to the page it was launched into, so
// navigating anywhere else drops it.
const PRESERVED_QUERY_PARAMS = ['appbar', 'sidebar', 'embed', 'ehr']

router.beforeEach((to, from) => {
  const preservedParams = {}
  PRESERVED_QUERY_PARAMS.forEach(param => {
    if (from.query[param] && !to.query[param]) {
      preservedParams[param] = from.query[param]
    }
  })

  if (Object.keys(preservedParams).length > 0) {
    return {
      ...to,
      query: {
        ...to.query,
        ...preservedParams
      }
    }
  }
})

// Prevent navigation to login when in EHR embed mode
router.beforeEach((to, from) => {
  if (to.path === '/login' && (to.query.ehr != null || from.query.ehr != null)) {
    const query = {};
    PRESERVED_QUERY_PARAMS.forEach(param => {
      const val = to.query[param] || from.query[param];
      if (val != null) query[param] = val;
    });
    if (!store.ehrAuthError) {
      store.ehrAuthError = 'Your session has expired or could not be authenticated.';
    }
    return { path: '/apps', query };
  }
})

router.beforeResolve(async (to) => {
  const otc = to.query.otc;
  if (otc) {
    const queryWithoutOtc = { ...to.query };
    delete queryWithoutOtc.otc;
    const isEmbed = to.query.embed != null || to.query.ehr != null;

    try {
      const result = await coreApi.useOneTimeCode(otc);
      if (isEmbed && result && result.token) {
        setEmbedToken(result.token);
      }
    } catch (err) {
      console.error('Failed to use one time code');
      if (to.query.ehr != null) {
        store.ehrAuthError = err.message || 'Authentication failed';
        const query = {};
        PRESERVED_QUERY_PARAMS.forEach(param => {
          if (to.query[param] != null) query[param] = to.query[param];
        });
        return { path: '/apps', query };
      }
    }

    return { path: to.path, query: queryWithoutOtc };
  }
})

// Billing lockout — redirect all non-billing routes when account is locked
router.beforeEach((to) => {
  if (store.billingAlert?.status === 'locked' && !store.isDashQuillAdmin) {
    const allowed = ['/settings/billing', '/login', '/register']
    if (!allowed.some(p => to.path.startsWith(p))) {
      return { path: '/settings/billing' }
    }
  }
})

export default router;